import type { TypographyVariantsOptions } from "@mui/material/styles";
import {
  bodyScale,
  buttonScale,
  fontFamily,
  fontWeight,
  headingScale,
  inputLabelScale,
} from "../tokens/typography";
import { breakpoints } from "../tokens/breakpoints";

const desktopMq = `@media (min-width: ${breakpoints.sm}px)`;

const heading = (variant: keyof typeof headingScale, weight: number) => {
  const { fontSize, lineHeight, desktop } = headingScale[variant];
  return {
    fontFamily: "var(--rmh-font-header)",
    fontWeight: weight,
    fontSize,
    lineHeight,
    [desktopMq]: { fontSize: desktop.fontSize, lineHeight: desktop.lineHeight },
  };
};

const body = (
  variant: keyof typeof bodyScale,
  weight: keyof typeof fontWeight
) => {
  const bodySizeVarient = bodyScale[variant];
  return bodySizeVarient[weight];
};

const button = (variant: keyof typeof buttonScale) => buttonScale[variant];

const inputLabel = (variant: keyof typeof inputLabelScale) =>
  inputLabelScale[variant];

export const typography: TypographyVariantsOptions = {
  fontFamily: "var(--rmh-font-text)",
  fontWeightLight: fontWeight.light,
  fontWeightRegular: fontWeight.regular,
  fontWeightMedium: fontWeight.medium,
  fontWeightBold: fontWeight.bold,
  h1: heading("h1", fontWeight.regular),
  h2: heading("h2", fontWeight.regular),
  h3: heading("h3", fontWeight.regular),
  h4: heading("h4", fontWeight.regular),
  h5: heading("h5", fontWeight.regular),
  h6: heading("h6", fontWeight.regular),
  bodyXsLight: body("xs", "light"),
  bodyXsRegular: body("xs", "regular"),
  bodyXsMedium: body("xs", "medium"),
  bodyXsSemiBold: body("xs", "semiBold"),
  bodyXsBold: body("xs", "bold"),
  bodySmLight: body("sm", "light"),
  bodySmRegular: body("sm", "regular"),
  bodySmMedium: body("sm", "medium"),
  bodySmSemiBold: body("sm", "semiBold"),
  bodySmBold: body("sm", "bold"),
  bodyMdLight: body("md", "light"),
  bodyMdRegular: body("md", "regular"),
  bodyMdMedium: body("md", "medium"),
  bodyMdSemiBold: body("md", "semiBold"),
  bodyMdBold: body("md", "bold"),
  bodyLgLight: body("lg", "light"),
  bodyLgRegular: body("lg", "regular"),
  bodyLgMedium: body("lg", "medium"),
  bodyLgSemiBold: body("lg", "semiBold"),
  bodyLgBold: body("lg", "bold"),
  bodyXlLight: body("xl", "light"),
  bodyXlRegular: body("xl", "regular"),
  bodyXlMedium: body("xl", "medium"),
  bodyXlSemiBold: body("xl", "semiBold"),
  bodyXlBold: body("xl", "bold"),
  buttonSm: button("sm"),
  inputLabelMd: inputLabel("md"),
};

/**
 * Last-resort fallback for `theme.typography.fontFamily` if CSS variables fail
 * to load (e.g. SSR before hydration without next/font). Not used by the theme
 * directly — exposed for tests/diagnostics.
 */
export const cssFontFamilyFallback = fontFamily;
